/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import PropTypes from "prop-types"; // Npm: prop-types library.
import _ from "underscore"; // Npm: underscore.js library.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI Data Grid.
import { useLazyQuery, useQuery } from "@apollo/client"; // Npm: Apollo client.
import { connect } from "react-redux"; // Npm: React Redux for state management.
import { useHistory } from "react-router-dom"; // Npm: React router dom.
import { HiArrowUturnDown, HiArrowPath, HiPresentationChartLine } from "react-icons/hi2"; // Npm: react-icons library.
import { Box, Flex, Text, Button, useBreakpointValue } from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import DateRangePicker from "components/DateRangePicker";
import DownloadToExcel from "components/DownloadToExcel";
import TableSpinner from "components/TableSpinner";
import RouteTrafficReport from "./RouteTraffic";
import { DlrCell, ReportNameCell, SerialExpandCell } from "./report.cells";
import {
  buildDestinationRows,
  buildTrunkTotals,
  formatMoney,
  getAccountDisplayName,
  getReportBody,
  getReportTotals,
  mergeAccountsWithReportKeys,
  toAlphabetLabel,
  toDlrPercentage,
  toNumber,
} from "./report.utils";

/*
 * GRAPHS
 */
import CustomerReadQuery from "../Customer/__query__/index.customer.read.query";
import CustomerAccountReadQuery from "../Customer/Margin/__query__/index.customerAccount.read.query";
import CustomerMarginReportQuery from "../Customer/Margin/__query__/index.customer.marginReport.query";

const Index = ({ PassOnUpdate }) => {
  const _historyRef = useHistory();

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilterModel, 500),
    [],
  );
  React.useEffect(
    () => () => _setFilterDebounced.cancel(),
    [_setFilterDebounced],
  );
  const [sortModel, setSortModel] = React.useState([]);
  const [expandedCustomerId, setExpandedCustomerId] = React.useState(void 0);
  const [expandedTrunkId, setExpandedTrunkId] = React.useState(void 0);
  const [customerAccountCache, setCustomerAccountCache] = React.useState({});
  const [customerMarginCache, setCustomerMarginCache] = React.useState({});
  const [loadingCustomers, setLoadingCustomers] = React.useState({});
  const [loadingTrunks, setLoadingTrunks] = React.useState({});
  const [selectedRouteTrafficCustomer, setSelectedRouteTrafficCustomer] =
    React.useState(void 0);

  const [dateRange, setDateRange] = React.useState({
    selection: {
      startDate: Object.DateRangeProvider()?.startDate,
      endDate: Object.DateRangeProvider()?.endDate,
      key: "selection",
    },
  });
  const _isFilterApplied = React.useMemo(
    () =>
      filterModel?.items?.some((i) => i.value != void 0 && i.value !== "") ||
      filterModel?.quickFilterValues?.some((i) => i != void 0 && i !== ""),
    [filterModel],
  );
  const _appliedFilter = React.useMemo(
    () => _isFilterApplied ? filterModel : null,
    [_isFilterApplied, filterModel],
  );
  const _customerReadVariables = React.useMemo(() => ({
    skip: paginationModel?.pageSize * paginationModel?.page,
    take: paginationModel?.pageSize,
    filter: _appliedFilter,
    smsStartDate: dateRange?.selection.startDate,
    smsEndDate: dateRange?.selection.endDate,
  }), [_appliedFilter, dateRange?.selection.endDate, dateRange?.selection.startDate, paginationModel?.page, paginationModel?.pageSize]);
  const _isPreviousPageRef = React.useRef(false);
  const _paginationModelRef = React.useRef(paginationModel);

  // Previous pagination uses Apollo cache-first so visited pages do not hit backend again.
  const _QueryCustomerRead = useQuery(CustomerReadQuery, {
    variables: _customerReadVariables,
    fetchPolicy: _isPreviousPageRef.current ?
      "cache-first" :
      Object.React.App.fetchPolicy,
  });
  const [ReadCustomerAccounts] = useLazyQuery(CustomerAccountReadQuery);
  const [ReadCustomerMarginReport] = useLazyQuery(CustomerMarginReportQuery);

  const _rowCountRef = React.useRef(0);
  const _tableHeaderHeightRef = React.useRef(0);
  const _customerAccountRequestRef = React.useRef({});
  const _customerMarginRequestRef = React.useRef({});
  const _isCurrentViewMobile =
    useBreakpointValue({ base: true, md: false }, { fallback: "md" }) ?? false;

  const _ResetExpandedReport = React.useCallback(() => {
    _customerAccountRequestRef.current = {};
    _customerMarginRequestRef.current = {};
    setCustomerAccountCache({});
    setCustomerMarginCache({});
    setExpandedCustomerId(void 0);
    setExpandedTrunkId(void 0);
  }, []);

  const _HandleRefresh = React.useCallback(() => {
    _ResetExpandedReport();
    _QueryCustomerRead.refetch();
  }, [_QueryCustomerRead.refetch, _ResetExpandedReport]);
  const _HandlePaginationModelChange = React.useCallback((__paginationModel) => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize;

    _paginationModelRef.current = __paginationModel;
    setPaginationModel(__paginationModel);
  }, []);
  const _HandleColumnVisibilityModelChange = React.useCallback((newModel) =>
    setColumnVisibilityModel(newModel), []);
  React.useEffect(() => {
    _ResetExpandedReport();
  }, [
    _ResetExpandedReport,
    dateRange?.selection?.endDate,
    dateRange?.selection?.startDate,
  ]);

  // Child data is loaded only after a row expands, then cached by customer.
  const _LoadCustomerAccounts = React.useCallback(
    (customerId) => {
      if (_.isEmpty(customerId) || customerAccountCache?.[customerId]) return;
      if (_customerAccountRequestRef.current?.[customerId])
        return _customerAccountRequestRef.current[customerId];

      setLoadingCustomers((current) => ({ ...current, [customerId]: true }));

      _customerAccountRequestRef.current[customerId] = (async () => {
        const { data } = await ReadCustomerAccounts({
          variables: { customerId },
          fetchPolicy: Object.React.App.fetchPolicy,
        });

        setCustomerAccountCache((current) => ({
          ...current,
          [customerId]: _.compact(data?.CustomerAccountRead ?? []),
        }));
      })().finally(() => {
        delete _customerAccountRequestRef.current[customerId];
        setLoadingCustomers((current) => ({ ...current, [customerId]: false }));
      });

      return _customerAccountRequestRef.current[customerId];
    },
    [ReadCustomerAccounts, customerAccountCache],
  );

  const _LoadCustomerMargin = React.useCallback(
    (customerId, accountId) => {
      if (_.isEmpty(customerId) || customerMarginCache?.[customerId]) return;

      const _loadingKey = `${customerId}:${accountId}`;
      setLoadingTrunks((current) => ({ ...current, [_loadingKey]: true }));

      const _existingRequest = _customerMarginRequestRef.current?.[customerId];
      if (_existingRequest) {
        return _existingRequest.finally(() => {
          setLoadingTrunks((current) => ({ ...current, [_loadingKey]: false }));
        });
      }

      _customerMarginRequestRef.current[customerId] = (async () => {
        const { data } = await ReadCustomerMarginReport({
          variables: {
            customerId,
            startDate: dateRange.selection.startDate,
            endDate: dateRange.selection.endDate,
          },
          fetchPolicy: Object.React.App.fetchPolicy,
        });

        setCustomerMarginCache((current) => ({
          ...current,
          [customerId]: data?.CustomerMarginReport ?? {},
        }));
      })().finally(() => {
        delete _customerMarginRequestRef.current[customerId];
        setLoadingTrunks((current) => ({ ...current, [_loadingKey]: false }));
      });

      return _customerMarginRequestRef.current[customerId];
    },
    [
      ReadCustomerMarginReport,
      customerMarginCache,
      dateRange.selection.endDate,
      dateRange.selection.startDate,
    ],
  );

  const _HandleCustomerToggle = React.useCallback(
    async (customerId) => {
      if (expandedCustomerId === customerId) {
        setExpandedCustomerId(void 0);
        setExpandedTrunkId(void 0);
        return;
      }

      setExpandedCustomerId(customerId);
      setExpandedTrunkId(void 0);
      await Promise.all([
        _LoadCustomerAccounts(customerId),
        _LoadCustomerMargin(customerId, "customer"),
      ]);
    },
    [_LoadCustomerAccounts, _LoadCustomerMargin, expandedCustomerId],
  );

  const _HandleTrunkToggle = React.useCallback(
    async (customerId, accountId) => {
      const _trunkKey = `${customerId}:${accountId}`;

      if (expandedTrunkId === _trunkKey) {
        setExpandedTrunkId(void 0);
        return;
      }

      setExpandedTrunkId(_trunkKey);
      await _LoadCustomerMargin(customerId, accountId);
    },
    [_LoadCustomerMargin, expandedTrunkId],
  );

  const _HandleOpenRouteTraffic = React.useCallback(
    (row) => {
      setSelectedRouteTrafficCustomer({
        customerId: row?.customerId,
        companyName: row?.["Company.displayName"],
        currency: row?.currency,
        startDate: dateRange?.selection?.startDate,
        endDate: dateRange?.selection?.endDate,
      });
    },
    [dateRange?.selection?.endDate, dateRange?.selection?.startDate],
  );

  const _HandleBackToCustomerReport = React.useCallback(() => {
    setSelectedRouteTrafficCustomer(void 0);
  }, []);

  const _HandleDestinationClick = React.useCallback((row) => {
    PassOnUpdate({
      customerId: row?.customerId,
      companyName: row?.companyName,
      currency: row?.currency,
      countryName: row?.countryName,
      customerAccountSmppName: row?.customerAccountSmppName,
      startDate: dateRange.selection.startDate,
      endDate: dateRange.selection.endDate,
      _navigatedToMessage: true,
    });
    _historyRef.push("/reports/message");
  }, [PassOnUpdate, _historyRef, dateRange.selection.endDate, dateRange.selection.startDate]);

  // Data assignment.
  const columns = React.useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        align: "center",
        headerAlign: "center",
        filterable: true,
        hide: true,
      },
      {
        field: "sno",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        width: 90,
        sortable: false,
        disableColumnMenu: true,
        filterable: false,
        renderCell: params => (
          <SerialExpandCell
            {...params}
            expandedCustomerId={expandedCustomerId}
            expandedTrunkId={expandedTrunkId}
            loadingTrunks={loadingTrunks}
            onCustomerToggle={_HandleCustomerToggle}
            onTrunkToggle={_HandleTrunkToggle}
          />
        ),
      },
      {
        field: "Company.displayName",
        headerName: "Company Name",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        flex: 1,
        headerAlign: "center",
        minWidth: 260,
        renderCell: ReportNameCell,
      },
      {
        field: "type",
        headerName: "Customer Type",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        minWidth: 160,
        type: "singleSelect",
        valueOptions: _.without(
          Object.React.App.enums.CUSTOMER_TYPE.enums?.map((i) => i?.key),
          "TERMINATION",
        ),
      },
      {
        field: "countryName",
        headerName: "Country",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 140,
        renderCell: ({ row, value }) => {
          if (row?.hierarchyLevel !== 2 || row?.isTotalRow || row?.isLoadingRow)
            return value ?? "";

          return (
            <Button
              variant="link"
              color="#6366F1"
              fontSize="13px"
              fontWeight={700}
              onClick={(event) => {
                event.stopPropagation();
                _HandleDestinationClick(row);
              }}
            >
              {value}
            </Button>
          );
        },
      },
      {
        field: "totalSms",
        headerName: "Total SMS",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 100,
      },
      {
        field: "smsDelivered",
        headerName: "Delivered",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "smsFailed",
        headerName: "Failed",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "smsPending",
        headerName: "Pending",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "smsUndelivered",
        headerName: "Undelivered",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 140,
      },
      {
        field: "dlrPercentage",
        headerName: "DLR%",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 130,
        renderCell: DlrCell,
      },
      {
        field: "revenue",
        headerName: "Revenue",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 110,
      },
      {
        field: "cost",
        headerName: "Cost",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 110,
      },
      {
        field: "margin",
        headerName: "Margin",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 110,
      },
      {
        field: "routeTraffic",
        headerName: "Route Traffic",
        editable: false,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        minWidth: 150,
        renderCell: ({ row }) => {
          if (row?.hierarchyLevel !== 0) return "";

          return (
            <Button
              size="xs"
              borderRadius="8px"
              bg="purple.50"
              color="purple"
              _hover={{ bg: "purple.800", color: "white" }}
              leftIcon={<HiPresentationChartLine />}
              onClick={(event) => {
                event.stopPropagation();
                _HandleOpenRouteTraffic(row);
              }}
            >
              Traffic
            </Button>
          );
        },
      },
    ],
    [
      _HandleOpenRouteTraffic,
      _HandleDestinationClick,
      _HandleCustomerToggle,
      _HandleTrunkToggle,
      expandedCustomerId,
      expandedTrunkId,
      loadingTrunks,
    ],
  );

  const customerRows = React.useMemo(
    () =>
      _.compact(
        _QueryCustomerRead?.data?.CustomerRead?.map((r, index) => {
          if ("READ_SUCCESSFUL" !== r.status) return null;

          const _stats = r?._smsCountForGivenCustomer?.[r?.id] ?? {};

          return {
            id: `customer-${r?.id ?? `customer-report-${paginationModel.page}-${paginationModel.pageSize}-${index}`}`,
            customerId: r?.id,
            hierarchyLevel: 0,
            sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
            "Company.displayName": r?.Company?.displayName ?? "-",
            type: r?.type ?? "0",
            countryName: "",
            totalSms: toNumber(_stats?.smsCount),
            smsDelivered: toNumber(_stats?.smsDelivered),
            smsFailed: toNumber(_stats?.smsFailed),
            smsPending: toNumber(_stats?.smsAccepted),
            smsUndelivered: toNumber(_stats?.smsUndelivered),
            dlrPercentage: toDlrPercentage(
              _stats?.smsDelivered,
              _stats?.smsCount,
            ),
            revenue: "0",
            cost: "0",
            margin: "0",
            currency: r?.Billing?.currency,
          };
        }),
      ),
    [
      _QueryCustomerRead?.data?.CustomerRead,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );

  const rows = React.useMemo(() => {
    const _rows = [];

    // DataGrid is flat, so expanded child levels are inserted after their parent row.
    customerRows.forEach((customer) => {
      _rows.push(customer);

      if (expandedCustomerId !== customer.customerId) return;

      const _marginReportData = customerMarginCache?.[customer.customerId];
      const _marginReport = getReportBody(_marginReportData);
      const _marginTotals = getReportTotals(_marginReportData);
      const _cachedAccounts = customerAccountCache?.[customer.customerId] ?? [];
      const _isAccountLoading = Boolean(loadingCustomers?.[customer.customerId]);
      const _isMarginLoading = Boolean(
        loadingTrunks?.[`${customer.customerId}:customer`],
      );
      const _accounts =
        _isAccountLoading && _.isEmpty(_cachedAccounts)
          ? []
          : mergeAccountsWithReportKeys(_cachedAccounts, _marginReport);

      if (
        (_isAccountLoading && _.isEmpty(_accounts)) ||
        (_isMarginLoading && !_marginReportData)
      ) {
        _rows.push({
          id: `customer-${customer.customerId}-loading-trunks`,
          customerId: customer.customerId,
          hierarchyLevel: 1,
          sno: "",
          "Company.displayName": "",
          type: "",
          countryName: "",
          totalSms: "",
          smsDelivered: "",
          smsFailed: "",
          smsPending: "",
          smsUndelivered: "",
          dlrPercentage: "-",
          revenue: "",
          cost: "",
          margin: "",
          isLoadingRow: true,
        });
        return;
      }

      _accounts
        .map((account) => {
          const destinations = buildDestinationRows(_marginReport, account?.id);
          const trunkTotals = buildTrunkTotals(destinations);

          return {
            account,
            destinations,
            trunkTotals,
            canExpand: trunkTotals.totalSms > 0,
          };
        })
        .filter(({ canExpand }) => canExpand)
        .sort((a, b) =>
          getAccountDisplayName(a.account).localeCompare(
            getAccountDisplayName(b.account),
          ),
        )
        .forEach(({ account, destinations, trunkTotals }, accountIndex) => {
          const _trunkKey = `${customer.customerId}:${account?.id}`;

          _rows.push({
            id: `customer-${customer.customerId}-trunk-${account?.id}`,
            customerId: customer.customerId,
            accountId: account?.id,
            companyName: customer?.["Company.displayName"],
            currency: customer?.currency,
            customerAccountSmppName: getAccountDisplayName(account),
            hierarchyLevel: 1,
            sno: toAlphabetLabel(accountIndex),
            "Company.displayName": getAccountDisplayName(account),
            type: "",
            countryName: "",
            totalSms: trunkTotals.totalSms,
            smsDelivered: trunkTotals.delivered,
            smsFailed: trunkTotals.failed,
            smsPending: trunkTotals.accepted,
            smsUndelivered: "",
            dlrPercentage: toDlrPercentage(trunkTotals.delivered, trunkTotals.totalSms),
            revenue: "0",
            cost: "0",
            margin: "0",
            canExpand: true,
          });

          if (expandedTrunkId !== _trunkKey) return;

          if (!_marginReportData) {
            _rows.push({
              id: `customer-${customer.customerId}-trunk-${account?.id}-loading-destinations`,
              customerId: customer.customerId,
              accountId: account?.id,
              hierarchyLevel: 2,
              sno: "",
              "Company.displayName": "",
              type: "",
              countryName: "",
              totalSms: "0",
              smsDelivered: "0",
              smsFailed: "0",
              smsPending: "0",
              smsUndelivered: "0",
              dlrPercentage: "0",
              revenue: "0",
              cost: "0",
              margin: "0",
              isLoadingRow: true,
            });
            return;
          }

          destinations.forEach((destination) => {
            _rows.push({
              id: `customer-${customer.customerId}-trunk-${account?.id}-destination-${destination.countryName}`,
              customerId: customer.customerId,
              accountId: account?.id,
              companyName: customer?.["Company.displayName"],
              currency: customer?.currency,
              customerAccountSmppName: getAccountDisplayName(account),
              hierarchyLevel: 2,
              sno: "",
              "Company.displayName": "",
              countryName: destination.countryName,
              type: "",
              totalSms: destination.totalsmscount,
              smsDelivered: destination.totaldelivered,
              smsFailed: destination.totalfailed,
              smsPending: destination.totalaccepted,
              smsUndelivered: "-",
              dlrPercentage: destination.dlrPercentage,
              revenue: formatMoney(destination.revenue),
              cost: formatMoney(destination.cost),
              margin: formatMoney(destination.margin),
            });
          });

          _rows.push({
            id: `customer-${customer.customerId}-trunk-${account?.id}-destination-total`,
            customerId: customer.customerId,
            accountId: account?.id,
            hierarchyLevel: 2,
            sno: "Total",
            "Company.displayName": "",
            countryName: "",
            type: "",
            totalSms: "",
            smsDelivered: "",
            smsFailed: "",
            smsPending: "",
            smsUndelivered: "",
            dlrPercentage: "",
            revenue: formatMoney(_marginTotals?.totalRevenue),
            cost: formatMoney(_marginTotals?.totalCost),
            margin: formatMoney(_marginTotals?.totalMargin),
            isTotalRow: true,
          });
        });
    });

    return _rows;
  }, [
    customerAccountCache,
    customerMarginCache,
    customerRows,
    expandedCustomerId,
    expandedTrunkId,
    loadingCustomers,
  ]);
  const _rowCount = _.first(_QueryCustomerRead?.data?.CustomerRead)?._totalCount;
  const rowCount = React.useMemo(() => {
    if (undefined !== _rowCount && null !== _rowCount)
      _rowCountRef.current = _rowCount;

    return _rowCountRef.current;
  }, [_rowCount]);
  const cellsData = React.useMemo(() => _QueryCustomerRead?.data?.CustomerRead?.map(
    (item, __index) =>
      "READ_SUCCESSFUL" === item.status
        ? {
            "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
            "Company Name": item?.Company?.displayName ?? "-",
            "Open SID":
              _.compact(
                _.flatten(
                  _.pluck(
                    _.flatten(
                      _.pluck(item.CustomerAccount, "Smpp"),
                    ),
                    "sourceAddress",
                  ),
                ),
              )?.join(", ") ?? "",
            "Total Sms":
              item?._smsCountForGivenCustomer?.[item?.id]?.smsCount ?? "-",
            "Sms Delivered":
              item?._smsCountForGivenCustomer?.[item?.id]?.smsDelivered ?? "-",
            "Sms Failed":
              item?._smsCountForGivenCustomer?.[item?.id]?.smsFailed ?? "-",
            "Sms Pending":
              item?._smsCountForGivenCustomer?.[item?.id]?.smsAccepted ?? "-",
            "Sms Undelivered":
              item?._smsCountForGivenCustomer?.[item?.id]?.smsUndelivered ?? "-",
            "Dlr Percentage": `${
              (
                (item?._smsCountForGivenCustomer?.[item?.id]
                  ?.smsDelivered /
                  item?._smsCountForGivenCustomer?.[item?.id]
                    ?.smsCount) *
                100
              )?.toFixed(2) ?? "-"
            }%`,
          }
        : void 0,
  ), [_QueryCustomerRead?.data?.CustomerRead, paginationModel.page, paginationModel.pageSize])
  const headersData = React.useMemo(() => [
    "S.No.",
    "Company Name",
    "Open SID",
    "Total Sms",
    "Sms Delivered",
    "Sms Failed",
    "Sms Pending",
    "Sms Undelivered",
    "Dlr Percentage",
  ].map((i) => ({ key: i, label: i })), [])
  const slots = React.useMemo(() => ({
    loadingOverlay: () => (
      <TableSpinner
        isLoading={true}
        isEmpty={false}
        chopHeightFromHundredPercentage={
          _tableHeaderHeightRef?.current?.clientHeight
        }
      />
    ),
    noResultsOverlay: () => (
      <TableSpinner
        isLoading={false}
        isEmpty={true}
        chopHeightFromHundredPercentage={
          _tableHeaderHeightRef?.current?.clientHeight
        }
      />
    ),
    noRowsOverlay: () => (
      <TableSpinner
        isLoading={false}
        isEmpty={true}
        chopHeightFromHundredPercentage={
          _tableHeaderHeightRef?.current?.clientHeight
        }
      />
    ),
  }), [])

  // Return component.
  return (
    <>
      <Flex
        className="reportsCustomer base"
        height="auto"
        flexDirection="column"
      >
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          height="100%"
          width="100%"
          overflow="hidden"
        >
          <Flex
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            bg="linear-Gradient(73deg,#390EF6 0%, #9244F4 100%)"
            borderTopLeftRadius="20px"
            borderTopRadius="20px"
            p={_isCurrentViewMobile ? "12px" : "22px"}
            flexShrink={0}
            minH="auto"
            gap="12px"
          >
            {selectedRouteTrafficCustomer?.customerId ? (
              <Flex gap="12px" alignItems="center" flexWrap="wrap">
                <Button
                  borderRadius="8px"
                  fontWeight="450"
                  minW="20px"
                  backgroundColor="rgba(244, 247, 254, 0.35)"
                  color="white"
                  _hover={{ bg: "white", color: "purple" }}
                  onClick={_HandleBackToCustomerReport}
                >
                  Customer Report
                </Button>
                <Text
                  as="h3"
                  fontSize={17}
                  fontWeight={500}
                  display="flex"
                  alignItems="center"
                  color="white"
                  gap="6px"
                  whiteSpace="nowrap"
                >
                  <HiPresentationChartLine />
                  Route Traffic - {selectedRouteTrafficCustomer.companyName}
                </Text>
              </Flex>
            ) : (
              <Flex gap="12px" alignItems="center" flexWrap="wrap">
                <Text
                  as="h3"
                  fontSize={17}
                  fontWeight={500}
                  display="flex"
                  alignItems="center"
                  color="white"
                  gap="6px"
                  whiteSpace="nowrap"
                >
                  <HiArrowUturnDown />
                  {_isCurrentViewMobile
                    ? "Filter Rows"
                    : "Filter Rows based on Date."}
                </Text>
                <DateRangePicker
                  startDate={dateRange?.selection?.startDate}
                  endDate={dateRange?.selection?.endDate}
                  w={{ base: "100%", md: "240px" }}
                  fontSize="13px"
                  height="max-content"
                  gap="6px"
                  setSelectDate={setDateRange}
                />
              </Flex>
            )}
            <Flex justifyContent="flex-end" gap="12px" alignItems="center">
              {!selectedRouteTrafficCustomer?.customerId && (
                <>
                  <Button
                    borderRadius="8px"
                    fontWeight="450"
                    minW="20px"
                    backgroundColor="purple.200"
                    color="purple"
                    _hover={{ bg: "purple.800", color: "white" }}
                    onClick={_HandleRefresh}
                    isDisabled={_QueryCustomerRead?.loading}
                    leftIcon={<HiArrowPath />}
                  >
                    Refresh
                  </Button>
                  <DownloadToExcel
                    cellsData={cellsData}
                    headersData={headersData}
                  />
                </>
              )}
            </Flex>
          </Flex>
          <Flex
            height="calc(100vh - 240px)"
            minHeight="calc(100vh - 240px)"
            maxHeight="calc(100vh - 240px)"
            bg="white"
            flexDirection="column"
            border="2px solid #3A0FF6"
            borderTop="none"
            borderBottomRadius="15px"
            overflow="hidden"
            w="100%"
          >
            {!selectedRouteTrafficCustomer?.customerId ? (
              <DataGrid
                rows={rows}
                columns={columns}
                rowHeight={48}
                rowCount={rowCount}
                paginationMode="server"
                filterMode="server"
                sortingMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={_HandlePaginationModelChange}
                filterModel={filterModel}
                onFilterModelChange={_setFilterDebounced}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                pageSizeOptions={[10, 20, 50, 100]}
                slots={slots}
                disableRowSelectionOnClick
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={
                  _HandleColumnVisibilityModelChange
                }
                loading={_QueryCustomerRead?.loading}
                getRowClassName={(params) =>
                  `report-tree-level-${params.row?.hierarchyLevel ?? 0}${params.row?.isLoadingRow ? " report-tree-loading-row" : ""}${params.row?.isTotalRow ? " report-tree-total-row" : ""}`
                }
                sx={{
                  "& .MuiDataGrid-main": {
                    border: "none",
                    borderRadius: 0,
                  },
                  "& .MuiDataGrid-cell": {
                    alignItems: "center",
                    display: "flex",
                  },
                  "& .MuiDataGrid-row": {
                    transition:
                      "background-color 180ms ease, opacity 180ms ease, transform 180ms ease",
                  },
                  "& .report-tree-level-1": {
                    backgroundColor: "#FAFBFF",
                  },
                  "& .report-tree-level-2": {
                    backgroundColor: "#FFFFFF",
                  },
                  "& .report-tree-level-1 .MuiDataGrid-cell:first-of-type": {
                    borderLeft: "3px solid #E9D8FD",
                  },
                  "& .report-tree-level-2 .MuiDataGrid-cell:first-of-type": {
                    borderLeft: "3px solid #D8E3FC",
                  },
                  "& .report-tree-loading-row": {
                    opacity: 0.85,
                  },
                  "& .report-tree-total-row": {
                    backgroundColor: "#F8FAFC",
                    borderTop: "1px solid #D8E3FC",
                  },
                }}
              />
            ) : (
              <Box w="100%" h="100%" overflow="auto">
                <RouteTrafficReport
                  isEmbedded
                  customerOverride={selectedRouteTrafficCustomer}
                />
              </Box>
            )}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

/*
 * EXPORTS
 */
Index.propTypes = {
  PassOnUpdate: PropTypes.func.isRequired,
};

const _MapDispatchToProps = __dispatch => ({
  PassOnUpdate: u => __dispatch({ type: "PASS_ON_UPDATE", PassOn: u }),
});

export default connect(null, _MapDispatchToProps)(React.memo(Index));
